  // HALAL CALGARY - SHARED
  //
  // What every page needs, map or article: the loading overlay, topbar
  // offsets, dark mode, DOM builders, lazy images, site events and tooltips.
  // The map itself is app.js, loaded only where there is a map. Both are
  // classic scripts sharing the global lexical scope, so app.js uses names
  // declared here directly: this file must load first, and the two must
  // never declare the same name.

  // Article pages share this topbar and <head> but have no map or place
  // data, so map-only work (the places fetch, the placeholder animation) is
  // skipped. Read off the server-rendered body class so it cannot disagree
  // with what was rendered.
  const IS_ARTICLES_VIEW = document.body.classList.contains('articles-mode');

  // Server-decided too: only known crawler User-Agents - see isLikelyBot().
  const IS_BOT = document.body.classList.contains('bot');

  // The toggles' sun/moon and menu/close icons, read off the layout's
  // <template id="hc-icons"> so they match the server's markup exactly. See
  // dom.icons().
  const UI_ICONS = dom.icons();
  const UI_ICON_MOON = UI_ICONS.moon || '';
  const UI_ICON_SUN = UI_ICONS.sun || '';
  const UI_ICON_MENU = UI_ICONS.menu || '';
  const UI_ICON_CLOSE = UI_ICONS.close || '';

  /* THE ACTIVE CARD, POSITIONED BEFORE THE SPLASH LIFTS.
     On a place's URL, centre its server-rendered card (data-active-card)
     now rather than after app.js has the data. Same sums as
     scrollCardIntoView() in app.js, so its later smooth-scroll moves
     nothing. */
  (function () {
    const list = dom.id('place-list');
    const slug = list && list.getAttribute('data-active-card');
    const card = slug && document.getElementById('place-card-' + slug);

    if (!card || !list.clientHeight) return;

    const c = card.getBoundingClientRect();
    const l = list.getBoundingClientRect();
    const top = (c.top - l.top) - (list.clientHeight - c.height) / 2;

    list.scrollTop = Math.max(0, Math.min(top, list.scrollHeight - list.clientHeight));
  })();

  /* A DEAD PHOTO UNCOVERS WHAT IS UNDER IT.
     Reviewer avatars stack a photo over an initials circle
     (partials/review-avatar); a photo that fails is removed. error does not
     bubble, hence the capture phase, plus a sweep for images that failed
     before this ran (complete with no natural width). reviewNode() in app.js
     handles the avatars it builds. */
  function dropIfBroken(img) {
    if (img.complete && img.naturalWidth === 0) dom.remove(img);
  }

  document.addEventListener('error', event => {
    const target = event.target;

    if (target && target.tagName === 'IMG' && target.hasAttribute('data-remove-on-error')) dom.remove(target);
  }, true);

  dom.all('img[data-remove-on-error]').forEach(dropIfBroken);


  // The words the search placeholder types out ("Search for ..."): the
  // filter row's enabled categories then cuisines in use, from
  // window.__hcTags (App\Services\PlaceTags) - exactly what the search
  // answers to (queryCategories/queryCuisines in app.js), so it never
  // promises a filter that is switched off. The fixed list is for pages with
  // no row.
  const SEARCH_WORDS = (function () {
    const tags = window.__hcTags || {};
    const words = [];

    ['categories', 'cuisines'].forEach(kind => {
      const group = tags[kind] || {};

      Object.keys(group).forEach(slug => {
        const label = group[slug] && group[slug].label;

        if (label && words.indexOf(label) === -1) words.push(label);
      });
    });

    return words.length ? words : ['Restaurants', 'Groceries', 'Prayer Spaces', 'Clothing'];
  })();

  const loadingOverlay = dom.id('loading-overlay');

  // Thumbnail for a place with no (or a broken) image. Transparent
  // background so it sits on the "-fallback" classes' var(--brand-tint) wash.
  const DEFAULT_THUMB_SVG = '<svg viewBox="0 0 300 100" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Illustration of people sharing a meal">' +
    '<ellipse cx="150" cy="77" rx="60" ry="7.5" fill="#d8b088"/><path d="M104 73c0-10 7-16 16-16s16 6 16 16z" fill="#5aa77f"/><circle cx="120" cy="51" r="9.5" fill="#d9a882"/><path d="M164 73c0-10 7-16 16-16s16 6 16 16z" fill="#6fb894"/><circle cx="180" cy="51" r="9.5" fill="#b3805a"/><path d="M150 31a17 17 0 0 1 17 17v7c0 8 4 13 8 18h-50c4-5 8-10 8-18v-7a17 17 0 0 1 17-17z" fill="#3f8f6b"/><circle cx="150" cy="50" r="10.5" fill="#e0b48f"/><ellipse cx="120" cy="74" rx="8.5" ry="2.5" fill="#f6f0e6"/><ellipse cx="180" cy="74" rx="8.5" ry="2.5" fill="#f6f0e6"/><ellipse cx="150" cy="79" rx="10" ry="3" fill="#f6f0e6"/>' +
  '</svg>';
  /* The setting normally names a file (/assets/defaults/place-thumb.svg is
     the same drawing); the inline copy is for when the boot payload never
     arrived. Not proxied - it is our own origin. */
  const defaultThumbSrc = (window.__hcImages && window.__hcImages.placeThumb)
    || 'data:image/svg+xml,' + encodeURIComponent(DEFAULT_THUMB_SVG);

  /**
   * Any remote image URL, rewritten to go through /api/image.php (no
   * hotlinking third-party hosts). The one place that builds proxy URLs.
   *
   * @param opts.fallback false to 404 rather than substitute the illustration,
   *                      so the caller's onerror can react (avatars, not card
   *                      thumbnails) - see ImageProxyController
   * @param opts.mark     the CSS width this image is DISPLAYED at; stamps the
   *                      wordmark after resizing to it (doubled for retina) so
   *                      the mark is legible at that size. Omit for no stamp.
   * @param opts.width    the CSS width it is DISPLAYED at, with no stamp; asks
   *                      for a resized copy rather than the 1920px original.
   *                      Ignored when opts.mark is set.
   */
  function proxiedImageSrc(url, opts) {
    const prefix = opts && opts.fallback === false
      ? '/api/image.php?fallback=none&url='
      : '/api/image.php?url=';

    const mark = opts && opts.mark
      ? '&mark=1&mv=' + (window.__hcMarkVersion || 1)
        + '&w=' + Math.round(opts.mark * Math.min(2, window.devicePixelRatio || 1))
      : '';

    /* ALWAYS 2x, NEVER devicePixelRatio: the server renders these same
       thumbnail URLs without knowing the ratio, and a mismatch would refetch
       every card image when the script patches the list. (Marked images are
       script-only, so the mark path can use the real ratio.) */
    const width = !mark && opts && opts.width
      ? '&w=' + Math.round(opts.width * 2)
      : '';

    // Some stored URLs are already percent-encoded; decode then encode once
    // so they are not double-encoded (%20 -> %2520). A malformed escape
    // throws.
    let normalized = url;
    try {
      normalized = decodeURIComponent(url);
    } catch (err) {
      // malformed - use the raw value
    }
    return prefix + encodeURIComponent(normalized) + mark + width;
  }

  /**
   * A place photo, at the size the caller will show it.
   *
   * Deliberately UNMARKED: the small sizes are below Wordmark's MIN_WIDTH,
   * and the sheet header is the business's own photo. See thumbNode() in
   * app.js. `width` is the CSS width it is DISPLAYED at (52px list square up
   * to the ~354px sheet header); omitting it serves the original.
   */
  function thumbSrc(p, width) {
    if (!p.image) return defaultThumbSrc;

    return proxiedImageSrc(p.image, width ? { width: width } : undefined);
  }

  let borderDrawDone = false;
  let dataReady = false;

  // ---- Bootstrap: loading overlay ----

  const loadingBorder = dom.id('loading-border');

  /* The border's four bars (see "THE BORDER" in main.css), as Web
     Animations. Read ONCE, just after updateChromeOffsets() below has brought
     style up to date: getAnimations() must do that itself, and later - with
     the markers or list half-built - it cost a full style and layout pass
     (~45ms on a phone) each time. */
  let borderAnims = null;

  function borderAnimations() {
    if (!borderAnims) {
      borderAnims = loadingBorder && loadingBorder.getAnimations
        ? loadingBorder.getAnimations({ subtree: true })
        : [];
    }

    return borderAnims;
  }

  // Hides the splash once BOTH the border draw and the data are done.
  // No overlay on article pages.
  function maybeHideLoadingOverlay() {
    if (!borderDrawDone || !dataReady || !loadingOverlay) return;
    if (loadingOverlay.classList.contains('hidden')) return;

    // A border cut short by the timeout below is drawn whole for the fade.
    borderAnimations().forEach((anim) => anim.finish());
    loadingOverlay.classList.add('hidden');

    /* Out of the paint tree once the 0.4s fade is over - see "OPACITY ONLY"
       in main.css. A timer, not transitionend, which also bubbles up from
       the splash's contents. */
    setTimeout(() => loadingOverlay.classList.add('gone'), 400);

    // The map has finished starting up: boot.js loads Analytics after this.
    window.dispatchEvent(new Event('halalcalgary:ready'));
  }

  if (loadingBorder) {
    // All four bars end together; the first to report is enough.
    loadingBorder.addEventListener('animationend', () => {
      borderDrawDone = true;
      maybeHideLoadingOverlay();
    });
  }

  /* WHEN THE DATA BEATS THE ANIMATION, the animation concedes.
     app.js calls this when the data is in: the draw plays the rest of its
     own curve in about 320ms, so the line carries on from where it is, still
     on the compositor, and animationend arrives as usual. The timeout covers
     a hidden tab or no requestAnimationFrame, where animations do not run. */
  function finishLoadingBorder() {
    if (borderDrawDone || !loadingBorder) return;

    const done = () => {
      if (borderDrawDone) return;
      borderDrawDone = true;
      maybeHideLoadingOverlay();
    };

    /* IN A FRAME: getAnimations() brings style up to date, and app.js calls
       this at the busiest moment of the load, before it builds the markers
       and the list. */
    requestAnimationFrame(() => {
      borderAnimations().forEach((anim) => {
        const timing = anim.effect && anim.effect.getComputedTiming();
        const remaining = timing ? timing.endTime - (timing.localTime || 0) : 0;

        if (remaining > 320) anim.updatePlaybackRate(remaining / 320);
      });
    });

    setTimeout(done, 480);
  }

  const mainEl = dom.id('main');

  // ---- Layout: chrome height offsets ----

  // Keeps #main below the topbar, which wraps to two lines on narrow screens.
  function updateChromeOffsets() {
    if (!mainEl) return; // #main doesn't exist on articles pages

    const topbar = dom.id('topbar');
    if (!topbar) return;

    mainEl.style.top = `${topbar.offsetHeight}px`;
  }

  window.addEventListener('resize', updateChromeOffsets);
  updateChromeOffsets();
  // While style is fresh from the line above - see borderAnimations().
  borderAnimations();

  /* THE SHELL CANNOT BE LEFT SCROLLED.
     main.css sizes the map shell in dvh ("THE MAP SHELL FOLLOWS THE URL
     BAR"); this catches the gaps where iOS still leaves the document offset,
     which slides the topbar under the browser chrome. visualViewport fires
     when the URL bar moves (window resize does not always). Articles are
     exempt - their document scroll is the reader's position. Only acts when
     there is an offset to correct. */
  const isArticles = document.body.classList.contains('articles-mode');

  function unstickShell() {
    if (isArticles) return;

    const vv = window.visualViewport;
    // iOS can leave the visual viewport panned with scrollY at 0 (restored
    // tabs, after the keyboard closes), so both are checked.
    const offset = window.scrollY || window.scrollX
      || (vv && (vv.offsetTop || vv.offsetLeft));

    if (offset) {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  }

  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', unstickShell);
    window.visualViewport.addEventListener('scroll', unstickShell);
  }

  window.addEventListener('orientationchange', unstickShell);
  // pageshow, not load: a bfcache restore brings the old scroll back
  // without firing load.
  window.addEventListener('pageshow', unstickShell);
  // iOS resumes a long-frozen tab already scrolled, with none of the events
  // above. The deferred pass is needed because iOS may still be restoring
  // its own scroll state at resume.
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) return;
    unstickShell();
    setTimeout(unstickShell, 250);
  });
  window.addEventListener('focus', unstickShell);
  // The document never scrolls in map mode, so any scroll is the signal.
  window.addEventListener('scroll', unstickShell, { passive: true });
  // The map swallows drags, so any touch is also a chance to recover.
  document.addEventListener('touchstart', unstickShell, { passive: true, capture: true });
  document.addEventListener('touchend', unstickShell, { passive: true, capture: true });
  // Watchdog for when no event fires at all; cheap, and a no-op unless offset.
  if (!isArticles) {
    setInterval(() => {
      if (!document.hidden) unstickShell();
    }, 1000);
  }

  // The theme boot.js resolved; if it did not run, recompute the same
  // saved-choice-then-OS rule so the toggle and tiles share one decision.
  let prefersDarkMode = window.__halalCalgaryDarkModePreferred;
  if (typeof prefersDarkMode !== 'boolean') {
    let storedDarkPref = null;
    try { storedDarkPref = localStorage.getItem('halalCalgaryDarkMode'); } catch (err) { /* storage unavailable */ }
    prefersDarkMode = storedDarkPref === 'true' ? true
      : storedDarkPref === 'false' ? false
      : !!(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
  }
  document.documentElement.classList.toggle('dark', prefersDarkMode);

  // ---- Dark mode ----

  // boot.js has already set .dark; this keeps the toggle, tiles and saved
  // choice in sync with it.
  const DARK_MODE_KEY = 'halalCalgaryDarkMode';
  /* SHARED DOM BUILDERS
     Top level so both this file and app.js can use them. */
  function el(tag, className, text) {
    const node = document.createElement(tag);

    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;

    return node;
  }

  function fromTrustedMarkup(markup) {
    // Cache on the function, not a const: this is called during start-up,
    // before a const declared here would leave its temporal dead zone.
    const markupTemplates = fromTrustedMarkup.cache
      || (fromTrustedMarkup.cache = new Map());

    let parsed = markupTemplates.get(markup);

    if (!parsed) {
      // Input is only ever our own constants (icons, help text) - that is
      // what makes this safe. DOMParser on top builds in an inert document:
      // no scripts run, no loading starts.
      const doc = new DOMParser().parseFromString(markup, 'text/html');

      parsed = document.createDocumentFragment();

      Array.prototype.forEach.call(doc.body.childNodes, node => {
        parsed.appendChild(document.importNode(node, true));
      });

      markupTemplates.set(markup, parsed);
    }

    // Always a clone: appending the cached fragment itself would empty it.
    return parsed.cloneNode(true);
  }

  const darkModeToggle = dom.id('dark-mode-toggle');
  const themeColorMeta = dom.one('meta[name="theme-color"]');

  function updateDarkModeToggleUI(isDark) {
    // Shows the mode a click would switch TO. Patched, so the icon the server
    // drew is kept as it is when it already is the right one.
    dom.patch(darkModeToggle, isDark ? UI_ICON_SUN : UI_ICON_MOON);
    darkModeToggle.setAttribute('aria-label', isDark ? 'Turn off dark mode' : 'Turn on dark mode');
    darkModeToggle.title = isDark ? 'Switch to light mode' : 'Switch to dark mode';
    if (themeColorMeta) themeColorMeta.setAttribute('content', isDark ? '#1c1c1e' : '#ffffff');
  }

  // No storage write: only a click turns the OS guess into a saved choice.
  updateDarkModeToggleUI(prefersDarkMode);

  /* Whatever must react to a theme flip beyond .dark registers here - the
     map tiles in app.js, which article pages do not load. */
  const themeListeners = [];

  function onThemeChange(fn) {
    themeListeners.push(fn);
  }

  // Everything .dark alone cannot reach; on a click or an OS change.
  function syncTheme(isDark) {
    themeListeners.forEach(fn => fn(isDark));
    updateDarkModeToggleUI(isDark);
  }

  darkModeToggle.addEventListener('click', () => {
    const isDark = !document.documentElement.classList.contains('dark');
    // boot.js owns the class and the saved choice (see its THEME rules).
    if (window.__halalCalgarySetTheme) {
      // It dispatches halalcalgary:theme, which runs syncTheme below.
      window.__halalCalgarySetTheme(isDark);
    } else {
      // boot.js blocked or failed; keep the button working.
      document.documentElement.classList.toggle('dark', isDark);
      try { localStorage.setItem(DARK_MODE_KEY, String(isDark)); } catch (err) { /* not remembered */ }
      syncTheme(isDark);
    }
  });

  // From boot.js on any change, including the OS flipping under an open tab.
  window.addEventListener('halalcalgary:theme', (event) => {
    syncTheme(!!(event.detail && event.detail.dark));
  });

  // On an article page app.js is not loaded, so nothing else will clear the
  // overlay.
  if (IS_ARTICLES_VIEW) {
    dataReady = true;
    maybeHideLoadingOverlay();
  }


/* LAZY IMAGES
   One loader for every image not needed for first paint. An <img> opts in
   with data-src; near the viewport (rootMargin lookahead) it moves to src and
   .is-loaded fades it in on the load event. Proxied photos can be slow or
   gone, so a failure has an explicit resting state, not a broken icon.
   window.hcLazy lets runtime-rendered markup (the sidebar list) hand over
   new images. */
window.hcLazy = (function () {
  'use strict';

  const DEFAULT_IMAGE = '/logo.png';
  const MARGIN = '400px 0px';
  // Keyed by scroll-container selector; a Map so no key collides with
  // Object.prototype.
  const observers = new Map();

  let loadedAny = false;
  let safetyTimer = null;

  function settle(img) {
    img.classList.add('is-loaded');
  }

  function load(img) {
    const src = img.getAttribute('data-src');
    if (!src) return;

    loadedAny = true;
    img.removeAttribute('data-src');

    img.addEventListener('load', () => settle(img), { once: true });

    img.addEventListener('error', () => {
      // Fall back to the logo unless opted out (the article hero keeps its
      // panel rather than a stretched logo).
      const fallback = img.getAttribute('data-fallback');

      if (fallback !== 'none') {
        img.src = fallback || DEFAULT_IMAGE;
        img.classList.add('is-fallback');
      }

      settle(img);
    }, { once: true });

    img.src = src;
  }

  /* One observer per scroll container: the sidebar scrolls inside
     #place-list, which a viewport observer would never see intersect. */
  function observerFor(rootSelector) {
    const key = rootSelector || '_viewport';

    if (!observers.has(key)) {
      const root = rootSelector ? dom.one(rootSelector) : null;

      observers.set(key, new IntersectionObserver((entries, self) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          load(entry.target);
          self.unobserve(entry.target);
        });
      }, { root, rootMargin: MARGIN, threshold: 0.01 }));
    }

    return observers.get(key);
  }

  /* Safe to call repeatedly - loading removes the data-src that selects an
     image. Re-queried each call to pick up newly rendered images. */
  function scan(container) {
    const images = dom.all('img[data-src]', container);

    if (!images.length) return;

    if (!('IntersectionObserver' in window)) {
      images.forEach(load);

      return;
    }

    images.forEach(img => observerFor(img.getAttribute('data-lazy-root')).observe(img));

    armSafetyNet();
  }

  /* CIRCUIT BREAKER
     An observer that never fires (no laid-out size, zero viewport, print or
     screenshot renderers) leaves every image invisible. If nothing at all has
     loaded shortly after a scan, abandon the observers and load everything.
     One successful load proves the observer works. */
  function armSafetyNet() {
    if (safetyTimer !== null || loadedAny) return;

    safetyTimer = window.setTimeout(() => {
      safetyTimer = null;

      if (loadedAny) return;

      const pending = dom.all('img[data-src]');
      if (!pending.length) return;

      observers.forEach(observer => observer.disconnect());
      observers.clear();

      pending.forEach(load);
    }, 2500);
  }

  function reset(rootSelector) {
    const key = rootSelector || '_viewport';
    const observer = observers.get(key);

    if (observer) {
      observer.disconnect();
      observers.delete(key);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan());
  } else {
    scan();
  }

  return { scan, load, reset };
})();


/* SITE EVENTS
   The app emits; listeners (analytics etc.) live in the custom widgets,
   editable in /admin. Widgets load at the end of the page, after app.js has
   already opened a /places/<slug>/ URL's place during boot - so emit()
   records and on() replays what was missed. Each event is also a real
   CustomEvent on `document` (hc:<name>), without replay. */

window.hcEvents = (function () {
  'use strict';

  // Bounded: replay only has to bridge the gap before widgets load.
  const MAX_REPLAY = 50;
  const past = [];
  // A Map: names come from editable widget code, so "__proto__" must be an
  // ordinary key.
  const handlers = new Map();

  function run(fn, name, detail) {
    try {
      fn(detail, name);
    } catch (err) {
      // One broken widget must not silence the others or break the app.
      if (window.console) console.error(`[hcEvents] handler for "${name}" failed:`, err);
    }
  }

  return {
    /** Announce that something happened. detail is whatever describes it. */
    emit(name, detail) {
      past.push({ name, detail });
      if (past.length > MAX_REPLAY) past.shift();

      (handlers.get(name) || []).forEach(fn => run(fn, name, detail));

      try {
        document.dispatchEvent(new CustomEvent(`hc:${name}`, { detail }));
      } catch (err) { /* CustomEvent unavailable - the callbacks above still ran */ }
    },

    /** Subscribe, and receive anything of this name that already happened. */
    on(name, fn) {
      if (typeof fn !== 'function') return;

      if (!handlers.has(name)) handlers.set(name, []);
      handlers.get(name).push(fn);

      past.forEach(e => {
        if (e.name === name) run(fn, name, e.detail);
      });
    },
  };
})();


/* NO ZOOM INTO A FOCUSED FIELD (iOS)
   The viewport allows zoom (see the layout), and Safari zooms the page into
   any text field under 16px as it takes focus - the search box is 13px -
   which shifts the topbar and, on the map, leaves Leaflet's idea of its size
   wrong. So the scale is capped at 1 from touchstart on a field (by focus it
   is too late) until nothing is focused. Pinch on the page is stopped
   separately: touch-action in main.css, gesture events in app.js. */
(function () {
  'use strict';

  const viewport = document.querySelector('meta[name="viewport"]');

  if (!viewport) return;

  const open = viewport.content;
  const isField = (node) => !!node && typeof node.matches === 'function'
    && node.matches('input:is(:not([type]), [type="text"], [type="search"], [type="email"], [type="url"], [type="tel"], [type="number"]), textarea, select');
  const release = () => {
    if (!isField(document.activeElement)) viewport.content = open;
  };

  document.addEventListener('touchstart', (e) => {
    if (isField(e.target)) viewport.content = open + ', maximum-scale=1';
  }, { capture: true, passive: true });
  document.addEventListener('focusout', () => setTimeout(release, 0));
  // A touch that never focused anything (a scroll that began on a field).
  document.addEventListener('touchend', () => setTimeout(release, 500), { passive: true });
})();


/* WebMCP plumbing (window.hcAgentTools) lives in mcp-tools.js. */


/* NO VISITOR BEACON HERE - the visit signal is the CSS heartbeat in main.css
   ("VISIT HEARTBEAT"). No script-based tracking on this site.
*/
/* TOOLTIPS
   One bubble appended to <body>, positioned per hover, so no ancestor can
   clip it (the cuisine row's overflow-x clips CSS pseudo-element tips) and it
   can flip above or slide in from screen edges. It adopts each element's
   `title` on first hover, removing the attribute so the native box does not
   double up. Hover devices only - on touch a tip flashes or sticks. */
(function () {
  const fine = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)');

  if (!fine || !fine.matches) return;

  // Long enough that sweeping across a row of icons stays quiet.
  const DELAY = 350;
  const GAP = 9;
  const EDGE = 8;

  let bubble = null;
  let timer = null;
  let current = null;

  function element() {
    if (bubble) return bubble;

    bubble = document.createElement('div');
    bubble.className = 'hc-tip';
    bubble.setAttribute('role', 'tooltip');
    bubble.hidden = true;
    dom.insert(document.body, bubble);

    return bubble;
  }

  /* `data-tip` is the deliberate form; `title` is adopted into it. */
  function label(el) {
    if (el.dataset.tip) return el.dataset.tip;

    const title = el.getAttribute('title');

    if (title && title.trim() !== '') {
      el.dataset.tip = title.trim();

      // Removing `title` can remove an icon button's only accessible name,
      // so keep it as aria-label where there is no other.
      if (! el.getAttribute('aria-label') && el.textContent.trim() === '') {
        el.setAttribute('aria-label', el.dataset.tip);
      }

      el.removeAttribute('title');

      return el.dataset.tip;
    }

    return '';
  }

  function place(el, text) {
    const tip = element();

    dom.text(tip, text);
    tip.hidden = false;
    tip.classList.remove('is-above');

    const target = el.getBoundingClientRect();
    const self = tip.getBoundingClientRect();

    // Below by default, above when there is no room below.
    const below = target.bottom + GAP + self.height <= window.innerHeight - EDGE;
    let top = below ? target.bottom + GAP : target.top - GAP - self.height;

    if (!below) tip.classList.add('is-above');

    // Centred, then clamped to the viewport; the caret stays on the target.
    let left = target.left + (target.width / 2) - (self.width / 2);

    left = Math.max(EDGE, Math.min(left, window.innerWidth - self.width - EDGE));

    const caret = target.left + (target.width / 2) - left;

    tip.style.top = Math.round(top) + 'px';
    tip.style.left = Math.round(left) + 'px';
    tip.style.setProperty('--tip-caret', Math.round(Math.max(10, Math.min(caret, self.width - 10))) + 'px');

    // Commit the position before the class lands, or the fade animates from
    // the old spot. A sync reflow leaves nothing to cancel, unlike a rAF.
    void tip.offsetHeight;
    tip.classList.add('is-visible');
  }

  function hide() {
    clearTimeout(timer);
    current = null;

    if (!bubble) return;

    bubble.classList.remove('is-visible');
    bubble.hidden = true;
  }

  function show(el) {
    const text = label(el);

    if (!text) return;

    /* MAP PINS ARE LABELLED, BUT NOT BUBBLED: the map shows its own preview
       card on hover. label() has already run, so the pin keeps its name and
       the native box stays suppressed. */
    if (el.classList.contains('leaflet-marker-icon')) return;

    current = el;
    clearTimeout(timer);
    timer = setTimeout(() => {
      if (current === el && el.isConnected) place(el, text);
    }, DELAY);
  }

  function target(node) {
    if (!node || !node.closest) return null;

    return node.closest('[data-tip], [title]');
  }

  // Delegated, so later-rendered nodes are covered.
  document.addEventListener('mouseover', (e) => {
    const el = target(e.target);

    if (!el) return;
    if (el === current) return;

    hide();
    show(el);
  }, true);

  document.addEventListener('mouseout', (e) => {
    const el = target(e.target);

    if (el && el === current) hide();
  }, true);

  // Keyboard focus shows it too; the rest can move the target.
  document.addEventListener('focusin', (e) => {
    const el = target(e.target);

    if (el) { hide(); show(el); }
  });

  document.addEventListener('focusout', hide);
  document.addEventListener('click', hide, true);
  window.addEventListener('scroll', hide, { passive: true, capture: true });
  window.addEventListener('resize', hide);
})();
